// External Dependencies
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { Center } from '@chakra-ui/react';
import { DisplayAggregateQuestionDto } from '@keyops-cep/api-client';
import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { MaxDiffAnswerDataWithDemographic } from '../../../pages/EngagementDetails/types';
import { CHART_COLORS } from '../../../utils/constants/visualization-constants';
import {
  calculateDatalabelFont,
  doesLabelFitInBar,
  getBarDimensions,
  getChartTitleConfig,
  getQuestionGraphFontSize,
} from '../../../utils/functions/chart-label-utils';
import {
  copyGraph,
  downloadGraph,
} from '../../../utils/functions/visualization-actions';
import { sortGraphResults } from '../../../utils/functions/visualization-utils';
import i18n from '../../../utils/i18n';
// Internal Dependencies
import { VisualizationChartRef } from '..';

type MaxDiffProps = {
  question: DisplayAggregateQuestionDto;
  questionAnswers: MaxDiffAnswerDataWithDemographic[];
  maintainAspectRatio?: boolean;
  isExpanded?: boolean;
};

type AttributeTallies = Record<string, { best: number; worst: number }>;
type Totals = { best: number; worst: number };

// ---------- Utils ----------
const oneDecimalNumberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatPercent(value: number): string | null {
  if (value <= 0) return null; // hide true zeros so empty rows stay empty
  if (value < 0.1) return '<0.1%'; // avoid showing "0.0%"
  return `${oneDecimalNumberFormatter.format(value)}%`; // e.g., "3.4%"
}

// ---------- Data aggregation ----------
export function buildAttributeTallies(
  attributeLabels: string[],
  answers: MaxDiffAnswerDataWithDemographic[]
): { attributeTallies: AttributeTallies; totals: Totals } {
  const attributeTallies: AttributeTallies = Object.fromEntries(
    attributeLabels.map((label) => [label, { best: 0, worst: 0 }])
  );

  for (const response of answers) {
    for (const step of response.components || []) {
      const answer = step.value;
      const bestPick = answer['best'];
      const worstPick = answer['worst'];

      if (attributeTallies[bestPick]) {
        attributeTallies[bestPick].best += 1;
      }
      if (attributeTallies[worstPick]) {
        attributeTallies[worstPick].worst += 1;
      }
    }
  }

  const totals: Totals = {
    best: Object.values(attributeTallies).reduce((sum, x) => sum + x.best, 0),
    worst: Object.values(attributeTallies).reduce((sum, x) => sum + x.worst, 0),
  };

  return { attributeTallies, totals };
}

export type MaxDiffRow = {
  label: string;
  best: number;
  worst: number;
  preferenceSharePercent: number;
};

// Compute Preference Shares via logit transform — https://www.questionpro.com/help/maxdiff-results.html
function computeBestWorstPreferenceShare(
  attributeLabels: string[],
  attributeTallies: AttributeTallies,
  totals: Totals
): MaxDiffRow[] {
  const totalBestCount = totals.best ?? 0;
  const totalWorstCount = totals.worst ?? 0;

  // Guard against 0/1 probabilities in the logit with a small Laplace-style epsilon
  const epsilonBest = totalBestCount > 0 ? 0.5 / (totalBestCount + 1) : 1e-6;
  const epsilonWorst = totalWorstCount > 0 ? 0.5 / (totalWorstCount + 1) : 1e-6;

  const clampProbability = (p: number, eps: number) =>
    Math.min(1 - eps, Math.max(eps, p));
  const logOdds = (p: number) => Math.log(p / (1 - p));

  const items = attributeLabels.map((label) => {
    const { best = 0, worst = 0 } = attributeTallies[label] ?? {
      best: 0,
      worst: 0,
    };

    // Step 1: Best proportion (proxy for % when shown)
    const bestProportion =
      totalBestCount > 0
        ? clampProbability(best / totalBestCount, epsilonBest)
        : 0.5; // neutral if no Best data
    const bestLogitUtility = totalBestCount > 0 ? logOdds(bestProportion) : 0;

    // Step 2: Worst proportion (proxy for % when shown) → negate its logit
    const worstProportion =
      totalWorstCount > 0
        ? clampProbability(worst / totalWorstCount, epsilonWorst)
        : 0.5; // neutral if no Worst data
    const worstLogitUtility =
      totalWorstCount > 0 ? -logOdds(worstProportion) : 0;

    // Step 3: Combine Best/Worst utilities (if one side missing, use the other)
    const availableParts =
      (totalBestCount > 0 ? 1 : 0) + (totalWorstCount > 0 ? 1 : 0);
    const combinedUtility =
      availableParts > 0
        ? (bestLogitUtility + worstLogitUtility) / availableParts
        : 0;

    return { label, best, worst, combinedUtility };
  });

  // Step 4: Zero-center utilities so the mean is 0
  const meanUtility = items.length
    ? items.reduce((sum, r) => sum + r.combinedUtility, 0) / items.length
    : 0;

  // Step 5: Exponentiate centered utilities (softmax numerators)
  const withNumerators = items.map((r) => {
    const centeredUtility = r.combinedUtility - meanUtility;
    const softmaxNumerator = Math.exp(centeredUtility);
    return { ...r, softmaxNumerator };
  });

  // Step 6: Normalize to get shares that sum to 100%
  const softmaxDenominator = withNumerators.reduce(
    (sum, r) => sum + r.softmaxNumerator,
    0
  );

  const result = withNumerators.map((r) => ({
    label: r.label,
    best: r.best,
    worst: r.worst,
    preferenceSharePercent: softmaxDenominator
      ? (r.softmaxNumerator / softmaxDenominator) * 100
      : 0,
  }));

  // Order by highest share first for display
  result.sort((a, b) => b.preferenceSharePercent - a.preferenceSharePercent);

  return result;
}

// ---------- Visual ----------
const MaxDiff = forwardRef<VisualizationChartRef, MaxDiffProps>(
  (
    {
      question,
      questionAnswers,
      maintainAspectRatio = false,
      isExpanded = false,
    },
    ref
  ) => {
    const chartRef = useRef<Chart<'bar', number[], string> | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useImperativeHandle(ref, () => ({
      onDownload: (name: string) => {
        downloadGraph(name, chartRef);
      },
      onCopyToClipboard: async () => {
        await copyGraph(chartRef);
      },
    }));

    useEffect(() => {
      const font = getQuestionGraphFontSize(isExpanded);

      const attributeLabels = (question.choices || []).map(
        (choice) => choice.label
      );

      const { attributeTallies, totals } = buildAttributeTallies(
        attributeLabels,
        questionAnswers
      );

      const rows = computeBestWorstPreferenceShare(
        attributeLabels,
        attributeTallies,
        totals
      );

      // sort the rows according to displayConfig
      const sortedRows = sortGraphResults(
        question.visualization,
        question.displayConfig?.orderBy,
        rows
      ) as MaxDiffRow[];

      if (!canvasRef.current) return;

      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      const labels = sortedRows.map((row) => row.label);
      const shareValues = sortedRows.map((row) => row.preferenceSharePercent);

      // For tooltip counts
      const countsByAttribute: Record<string, { best: number; worst: number }> =
        {};
      for (const row of sortedRows) {
        countsByAttribute[row.label] = { best: row.best, worst: row.worst };
      }

      chartRef.current = new Chart<'bar', number[], string>(canvasRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Preference Share %',
              data: shareValues,
              backgroundColor: CHART_COLORS[2],
              borderRadius: (context) => {
                const { height } = getBarDimensions(context);
                return Math.min(Math.floor(height * 0.3), isExpanded ? 10 : 5);
              },
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio,
          layout: { padding: { right: 35 } },
          indexAxis: 'y',
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              titleFont: font,
              bodyFont: font,
              callbacks: {
                label: (tooltipItem) => {
                  const attribute = tooltipItem.label as string;
                  const counts = countsByAttribute[attribute] || {
                    best: 0,
                    worst: 0,
                  };
                  return ` best: ${counts.best}  •  worst: ${counts.worst}`;
                },
              },
            },
            datalabels: {
              anchor: (context) =>
                doesLabelFitInBar(context) ? 'start' : 'end',
              align: 'end',
              color: (context) =>
                doesLabelFitInBar(context) ? 'white' : 'black',
              font: (context) => calculateDatalabelFont(context),
              formatter: (value: number) => formatPercent(value),
            },
            ...(question.displayConfig?.chartTitle && {
              title: getChartTitleConfig(
                question.displayConfig?.chartTitle,
                isExpanded
              ),
            }),
          },
          scales: {
            x: {
              ticks: {
                font,
              },
              title: {
                display: isExpanded,
                text:
                  question.displayConfig?.xAxisTitle ??
                  (i18n.t(
                    'visualization.maxDiff.percentageAxisLabel'
                  ) as string),
                font,
              },
              min: 0,
              max: 100,
              grid: { color: 'rgba(0, 0, 0, 0.05)' },
            },
            y: {
              ticks: { font },
              grid: { display: false },
              ...(question.displayConfig?.yAxisTitle && {
                title: {
                  display: isExpanded,
                  text: question.displayConfig?.yAxisTitle,
                  font,
                  padding: {
                    bottom: 20,
                  },
                },
              }),
            },
          },
        },
        plugins: [ChartDataLabels],
      });

      return () => {
        if (chartRef.current) {
          chartRef.current.destroy();
          chartRef.current = null;
        }
      };
    }, [question, questionAnswers, maintainAspectRatio, isExpanded]);

    return (
      <Center h="100%">
        <canvas ref={canvasRef} />
      </Center>
    );
  }
);

MaxDiff.displayName = 'MaxDiff';

export default MaxDiff;
